/**
 *  @class
 *  @function ProductCompareTable
 */
if (!customElements.get('product-compare-table')) {
  class ProductCompareTable extends HTMLElement {
    constructor() {
      super();
      this.max = Number(this.dataset.max) || 3;
      this.mobileMax = Number(this.dataset.mobileMax) || this.max;
      this.mobileMedia = window.matchMedia('(max-width: 767px)');
      this.payloadCache = new Map();
      this.renderRequestId = 0;
    }

    connectedCallback() {
      this.container = this.querySelector('.compare-lightbox--data');
      this.addEventListener('render-product-compare-modal', this.renderTable.bind(this));
    }

    renderTable() {
      this.classList.add('loading');
      this.fetchProducts(++this.renderRequestId);
    }

    getSelectedProducts() {
      try {
        const products = JSON.parse(window.localStorage.getItem('compare-products-vision'));
        const max = this.mobileMedia.matches ? this.mobileMax : this.max;
        return Array.isArray(products) ? products.slice(0, max) : [];
      } catch (error) {
        return [];
      }
    }

    async fetchProducts(requestId) {
      const compareProducts = this.getSelectedProducts();
      const comparisonTables = await Promise.all(
        compareProducts.map((product) => this.fetchProductComparison(product))
      );

      if (requestId !== this.renderRequestId) return;

      const renderedTables = comparisonTables.filter(Boolean);
      this.classList.remove('loading');
      this.container.style.setProperty('--columns', Math.max(renderedTables.length, 1));
      this.container.innerHTML = renderedTables.join('');

      requestAnimationFrame(() => this.equalHeights());
    }

    async fetchProductComparison(product) {
      const productUrl = this.getProductUrl(product?.url);
      if (!productUrl) return '';

      const payload = await this.getSpecPayload(productUrl);
      const specTable = payload ? this.buildSpecTable(payload) : '';

      return specTable || this.fetchLegacyComparison(productUrl);
    }

    getProductUrl(url) {
      try {
        const productUrl = new URL(url, window.location.origin);
        productUrl.searchParams.delete('view');
        productUrl.searchParams.delete('sections');
        productUrl.hash = '';
        return productUrl.toString();
      } catch (error) {
        return '';
      }
    }

    getSpecPayload(productUrl) {
      if (!this.payloadCache.has(productUrl)) {
        this.payloadCache.set(productUrl, this.fetchSpecPayload(productUrl));
      }

      return this.payloadCache.get(productUrl);
    }

    async fetchSpecPayload(productUrl) {
      try {
        const response = await fetch(productUrl, { method: 'GET' });
        if (!response.ok) return null;

        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const payloadElement = document.querySelector('script[data-product-compare-specs]');
        if (!payloadElement) return null;

        const payload = JSON.parse(payloadElement.textContent);
        if (!Array.isArray(payload?.items) || typeof payload.cardHtml !== 'string') return null;

        return payload;
      } catch (error) {
        return null;
      }
    }

    buildSpecTable(payload) {
      const fields = window.ieGeekProductCompareFields;
      if (!Array.isArray(fields) || !fields.length || !payload.cardHtml.trim()) return '';

      const mainItems = this.getMainSpecItems(payload.items);
      const itemIndex = this.createItemIndex(mainItems);
      const productId = payload.product?.id || '';

      const rows = fields.map((field) => {
        const value = this.resolveFieldValue(field, itemIndex);
        return `
          <div class="compare-lightbox--row" data-compare-field="${this.escapeAttribute(field.key)}">
            <div class="compare-lightbox--header">${this.escapeHtml(field.label)}</div>
            <div class="compare-lightbox--value">${this.formatValue(value)}</div>
          </div>`;
      }).join('');

      return `
        <div class="compare-lightbox--table" data-product-id="${this.escapeAttribute(productId)}">
          <div class="compare-lightbox--row">${payload.cardHtml}</div>
          <div class="compare-lightbox--fields">${rows}
          </div>
        </div>`;
    }

    getMainSpecItems(items) {
      const validItems = items.filter((item) => item && typeof item.label === 'string');
      const tabIndexes = validItems
        .map((item) => Number(item.tabIndex))
        .filter((tabIndex) => Number.isFinite(tabIndex) && tabIndex > 0);
      const mainTabIndex = tabIndexes.length ? Math.min(...tabIndexes) : 0;

      return validItems.filter((item) => Number(item.tabIndex) === mainTabIndex);
    }

    createItemIndex(items) {
      return items.reduce((index, item) => {
        const label = this.normalizeLabel(item.label);
        if (!label) return index;
        if (!index.has(label)) index.set(label, []);
        index.get(label).push(item.value);
        return index;
      }, new Map());
    }

    resolveFieldValue(field, itemIndex) {
      const aliases = Array.isArray(field.aliases) && field.aliases.length
        ? field.aliases
        : [field.label];

      for (const alias of aliases) {
        const values = itemIndex.get(this.normalizeLabel(alias)) || [];
        const value = values.find((candidate) => this.hasValue(candidate));
        if (value !== undefined) return value;
      }

      return 'N/A';
    }

    normalizeLabel(label) {
      return String(label || '')
        .normalize('NFKC')
        .replace(/\u00a0/g, ' ')
        .replace(/[‐‑‒–—−]/g, '-')
        .replace(/\s+/g, ' ')
        .replace(/\s*\)\s*$/, '')
        .trim()
        .toLowerCase();
    }

    hasValue(value) {
      const normalizedValue = String(value ?? '').trim();
      return normalizedValue !== '' && normalizedValue !== '-' && normalizedValue !== '—';
    }

    formatValue(value) {
      const output = this.hasValue(value) ? value : 'N/A';
      return this.escapeHtml(output).replace(/\r\n|\r|\n/g, '<br>');
    }

    escapeHtml(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    escapeAttribute(value) {
      return this.escapeHtml(value);
    }

    async fetchLegacyComparison(productUrl) {
      try {
        const legacyUrl = new URL(productUrl);
        legacyUrl.searchParams.set('view', 'compare');
        const response = await fetch(legacyUrl.toString(), { method: 'GET' });
        if (!response.ok) return '';
        return this.getSectionInnerHTML(await response.text(), '.compare-lightbox--data');
      } catch (error) {
        return '';
      }
    }

    getSectionInnerHTML(html, selector) {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector)?.innerHTML || '';
    }

    equalHeights() {
      const tables = Array.from(this.container.querySelectorAll('.compare-lightbox--table'));
      const rowCount = tables.reduce((maximum, table) => {
        return Math.max(maximum, table.querySelectorAll('.compare-lightbox--row').length);
      }, 0);

      for (let i = 0; i < rowCount; i++) {
        this.equalizeTableRowHeightAcrossTables(i);
      }
    }

    equalizeTableRowHeightAcrossTables(rowPosition) {
      const rows = Array.from(this.container.querySelectorAll('.compare-lightbox--table'))
        .map((table) => table.querySelectorAll('.compare-lightbox--row')[rowPosition])
        .filter(Boolean);
      const maximumHeight = Math.max(0, ...rows.map((row) => row.clientHeight));

      rows.forEach((row) => {
        row.style.height = `${maximumHeight}px`;
      });
    }
  }

  customElements.define('product-compare-table', ProductCompareTable);
}
